import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Star, Loader2, CloudOff, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { useAuth } from "@/context/AuthContext";

const REVIEW_STATUS = { published: "Sichtbar", hidden: "Versteckt", rejected: "Abgelehnt", pending: "Ausstehend" };

export default function Bewertungen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const scope = user?.role === "super_admin" ? "all" : "customers";
  const isSuper = user?.role === "super_admin";
  const [state, setState] = useState(null);

  const load = useCallback(() => {
    setState(null);
    api.get("/organizations/summary/all-reviews", { params: { scope } })
      .then((r) => setState(r.data)).catch(() => setState({ service_available: false, reviews: [] }));
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  const moderate = async (tid, rid, status) => {
    try { await api.put(`/organizations/${tid}/reviews/${rid}`, { status }); toast.success("Status aktualisiert"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (state === null) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>;
  const rows = state.reviews || [];

  return (
    <div data-testid="bewertungen-page">
      <PageHeader title="Bewertungen" subtitle="Kundenbewertungen über alle FUNDIO-Kunden – Moderation direkt aus CONTROL." />
      {!state.service_available ? (
        <div data-testid="bewertungen-unavailable" className="card-surface flex flex-col items-center rounded-xl px-6 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-status-danger-bg"><CloudOff className="h-6 w-6 text-status-danger" /></span>
          <p className="mt-4 text-base font-semibold text-fundio-ink">FUNDIO Tenant Service nicht erreichbar</p>
        </div>
      ) : (
        <Panel title={`Bewertungen (${rows.length})`} testid="bewertungen-panel">
          {!rows.length ? (
            <EmptyState icon={Star} title="Keine Bewertungen" description="Aktuell liegen keine Kundenbewertungen vor." testid="bewertungen-empty" />
          ) : (
            <div className="space-y-3">{rows.map((row, i) => {
              const r = row.review || {};
              const rating = r.rating ?? r.stars;
              return (
                <div key={r.id || i} data-testid={`bewertung-row-${r.id || i}`} className="rounded-xl border border-fundio-border bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <button onClick={() => navigate(`/brockis/${row.tenant_id}`)} className="flex items-center gap-1 text-sm font-semibold text-fundio-ink hover:text-fundio-gold-deep">{row.company_name || row.tenant_id}<ChevronRight className="h-3.5 w-3.5" /></button>
                      <div className="mt-1 flex items-center gap-2 text-xs text-fundio-muted">
                        {rating != null && <span className="inline-flex text-status-warning">{Array.from({ length: 5 }).map((_, j) => <Star key={j} className={`h-3 w-3 ${j < rating ? "fill-current" : "text-fundio-faint"}`} />)}</span>}
                        <span className="rounded border border-fundio-border bg-fundio-subtle px-1.5 py-0.5 text-[10px] font-semibold uppercase">{REVIEW_STATUS[r.status] || r.status}</span>
                      </div>
                      <div className="mt-1 max-w-2xl text-xs text-fundio-muted">{r.comment || r.text || r.body || "—"}</div>
                    </div>
                    {isSuper && (
                      <div className="flex shrink-0 gap-2">
                        <button data-testid={`bewertung-publish-${r.id}`} onClick={() => moderate(row.tenant_id, r.id, "published")} className="rounded-lg border border-status-success-border bg-status-success-bg px-2.5 py-1 text-xs font-semibold text-status-success">Sichtbar</button>
                        <button data-testid={`bewertung-hide-${r.id}`} onClick={() => moderate(row.tenant_id, r.id, "hidden")} className="rounded-lg border border-fundio-border bg-white px-2.5 py-1 text-xs font-semibold text-fundio-muted">Verstecken</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}</div>
          )}
        </Panel>
      )}
    </div>
  );
}
